import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LogOut, Menu, X } from "lucide-react";
import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { Brand } from "@/components/page";
import { api } from "@/lib/api";
import { initials } from "@/lib/format";
import type { Me } from "@/lib/session";
import { useNavHint } from "@/lib/nav-hint";
import { cn } from "@/lib/utils";

export type NavChild = { href: string; label: string };
export type NavItem = { href: string; label: string; icon: ComponentType<{ className?: string }>; badge?: number; children?: NavChild[]; also?: string[] };
export type NavGroup = { label?: string; items: NavItem[] };

function isActive(location: string, href: string) {
  return href === "/" ? location === "/" : location === href || location.startsWith(`${href}/`);
}

/** A section shares its service's address prefix, so the service's own page matches exactly. */
function childActive(location: string, parent: NavItem, child: NavChild) {
  if (child.href === parent.href) return location === child.href || !parent.children!.some((c) => c.href !== parent.href && isActive(location, c.href));
  return isActive(location, child.href);
}

/** App frame shared by the staff workspace and the client portal. */
export function Shell({ me, nav, children, firmName, floating }: { me: Me; nav: NavGroup[]; children: ReactNode; firmName?: string; floating?: ReactNode }) {
  const [location] = useLocation();
  const hint = useNavHint();
  const [open, setOpen] = useState(false);
  // A section folded up by clicking it again; it opens again on the next page change.
  const [folded, setFolded] = useState<string | null>(null);
  useEffect(() => setFolded(null), [location]);
  const qc = useQueryClient();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  const logout = useMutation({
    mutationFn: () => api.send("POST", "/api/auth/logout"),
    onSuccess: () => {
      qc.clear();
      window.location.assign("/sign-in");
    },
  });

  // Staff and client views are different apps, so a switch reloads from the home page.
  const switchFirm = useMutation({
    mutationFn: (firmId: string) => api.send("POST", "/api/auth/switch-firm", { firmId }),
    onSuccess: () => {
      qc.clear();
      window.location.assign("/");
    },
  });

  const sidebar = (
    <nav aria-label="Main" className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="border-b border-sidebar-border px-5 py-5">
        <Link href="/" onClick={() => setOpen(false)}>
          <Brand light />
        </Link>
        {(me.memberships?.length ?? 0) > 1 ? (
          <label className="mt-3 block">
            <span className="sr-only">Switch firm</span>
            <select
              value={me.activeFirmId ?? ""}
              disabled={switchFirm.isPending}
              onChange={(e) => switchFirm.mutate(e.target.value)}
              className="w-full truncate rounded-md border border-sidebar-border bg-sidebar-accent/60 px-2.5 py-1.5 text-xs text-sidebar-foreground focus:outline-none focus:ring-2 focus:ring-sidebar-ring"
            >
              {me.memberships!.map((m) => (
                <option key={m.firmId} value={m.firmId}>
                  {m.firmName}
                  {m.role === "client" ? " (client portal)" : ""}
                </option>
              ))}
            </select>
          </label>
        ) : (
          firmName && <p className="mt-2 truncate text-xs text-sidebar-foreground/60">{firmName}</p>
        )}
      </div>
      <div className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
        {nav.map((group, i) => (
          <div key={i}>
            {group.label && <p className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-widest text-sidebar-foreground/70">{group.label}</p>}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = isActive(location, item.href) || !!item.children?.some((c) => isActive(location, c.href)) || !!item.also?.some((h) => isActive(location, h)) || hint === item.href;
                const expanded = active && !!item.children?.length && folded !== item.href;
                return (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      onClick={(e) => {
                        if (expanded) {
                          // Clicked again while open: fold it up and stay on the page.
                          e.preventDefault();
                          setFolded(item.href);
                        } else if (item.children?.length) {
                          // Opening a section: on a phone, keep the menu open to pick a page in it.
                          setFolded(null);
                        } else setOpen(false);
                      }}
                      aria-expanded={item.children?.length ? expanded : undefined}
                      aria-current={active && !expanded ? "page" : undefined}
                      className={cn(
                        "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                        active ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : "text-sidebar-foreground/75 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
                      )}
                    >
                      <item.icon className="h-4 w-4 opacity-80" />
                      <span className="flex-1">{item.label}</span>
                      {!!item.badge && (
                        <span className="rounded-full bg-sidebar-primary px-2 py-0.5 text-[11px] font-semibold text-sidebar-primary-foreground">{item.badge}</span>
                      )}
                    </Link>
                    {expanded && (
                      <ul className="mb-1 mt-0.5 space-y-0.5 border-l border-sidebar-border pl-3 ml-5">
                        {item.children!.map((c) => {
                          const on = childActive(location, item, c);
                          return (
                            <li key={c.href}>
                              <Link
                                href={c.href}
                                onClick={() => setOpen(false)}
                                aria-current={on ? "page" : undefined}
                                className={cn(
                                  "block rounded-md px-3 py-1.5 text-[13px] transition-colors",
                                  on ? "font-medium text-sidebar-primary" : "text-sidebar-foreground/65 hover:text-sidebar-foreground",
                                )}
                              >
                                {c.label}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 border-t border-sidebar-border px-4 py-4">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sidebar-accent text-xs font-semibold text-sidebar-primary">
          {initials(me.user?.name ?? "?")}
        </span>
        <Link href="/profile" onClick={() => setOpen(false)} className="min-w-0 flex-1 rounded-md hover:opacity-90" title="Profile and security">
          <p className="truncate text-sm font-medium">{me.user?.name}</p>
          <p className="truncate text-xs text-sidebar-foreground/60">{me.user?.email}</p>
        </Link>
        <button onClick={() => logout.mutate()} className="rounded p-2 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-primary" title="Sign out" aria-label="Sign out">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-background">
      <a
        href="#main-content"
        onClick={(e) => {
          // A plain hash link would confuse the router; move focus to the page content directly.
          e.preventDefault();
          document.getElementById("main-content")?.focus();
        }}
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[60] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg"
      >
        Skip to content
      </a>
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 md:block">{sidebar}</aside>
      {open && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside id="mobile-nav" className="absolute inset-y-0 left-0 w-72 shadow-xl">{sidebar}</aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background/90 px-4 py-3 backdrop-blur-sm md:hidden">
          <Brand />
          <button onClick={() => setOpen((v) => !v)} aria-label="Menu" aria-expanded={open} aria-controls="mobile-nav" className="rounded p-2 hover:bg-muted">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </header>
        <main id="main-content" tabIndex={-1} className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 focus:outline-none md:px-8 md:py-8">{children}</main>
      </div>
      {/* Something that follows you from page to page, such as a running timer. */}
      {floating && <div className="fixed bottom-4 right-4 z-30 max-w-[calc(100vw-2rem)] rounded-full bg-background shadow-lg empty:hidden">{floating}</div>}
    </div>
  );
}
